import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Download, RefreshCw, Layers, Boxes,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const ALL_OPS = 'All Operations';

const OP_BADGE = {
  'Greige Inspection': 'bg-emerald-100 text-emerald-800',
  'Pinning': 'bg-blue-100 text-blue-800',
  'Marker Writing': 'bg-amber-100 text-amber-800',
  'Packing': 'bg-violet-100 text-violet-800',
  'Storage': 'bg-teal-100 text-teal-800',
  'Processing': 'bg-orange-100 text-orange-800',
  'Processing Inward': 'bg-emerald-100 text-emerald-800',
  'Folding': 'bg-blue-100 text-blue-800',
  'Damage Checking': 'bg-rose-100 text-rose-800',
  'Pressing': 'bg-amber-100 text-amber-800',
  'Assortment': 'bg-violet-100 text-violet-800',
};

/**
 * Operation Wise Stock Report — WIP stock bucketed by the last completed
 * operation per section. Section dropdown → cascading Operations dropdown
 * → universal text filter → CSV export.
 */
export default function OperationWiseStockReport() {
  const navigate = useNavigate();

  const [sections, setSections] = useState([]);
  const [operations, setOperations] = useState([]);
  const [section, setSection] = useState('');
  const [operation, setOperation] = useState(ALL_OPS);
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total_bundles: 0, total_quantity: 0, by_operation: {} });
  const [isLoading, setIsLoading] = useState(false);

  // Load sections, default to first
  useEffect(() => {
    apiClient.get('/reports/operation-wise-stock/sections')
      .then((r) => {
        const secs = r.data.sections || [];
        setSections(secs);
        if (secs.length) setSection(secs[0]);
      })
      .catch(() => toast.error('Failed to load sections'));
  }, []);

  // Cascading operations for the selected section
  useEffect(() => {
    if (!section) return;
    apiClient.get(`/reports/operation-wise-stock/operations?section=${encodeURIComponent(section)}`)
      .then((r) => {
        setOperations(r.data.operations || []);
        setOperation(ALL_OPS);
      })
      .catch(() => toast.error('Failed to load operations'));
  }, [section]);

  const fetchData = useCallback(async (opts = {}) => {
    const sec = opts.section ?? section;
    const op = opts.operation ?? operation;
    const q = opts.search ?? search;
    if (!sec) return;
    setIsLoading(true);
    try {
      const p = new URLSearchParams({ section: sec, operation: op });
      if (q) p.append('search', q);
      const res = await apiClient.get(`/reports/operation-wise-stock?${p.toString()}`);
      setRows(res.data.rows || []);
      setSummary(res.data.summary || { total_bundles: 0, total_quantity: 0, by_operation: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [section, operation, search]);

  // Auto-fetch when section/operation changes
  useEffect(() => {
    if (section && operation) fetchData({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, operation]);

  const handleExport = () => {
    if (rows.length === 0) {
      toast.error('No data to export');
      return;
    }
    // Native browser download (NOT fetch+blob): blob downloads triggered
    // from JS were silently blocked by some production browsers — the
    // direct navigation goes through the browser's download manager.
    const p = new URLSearchParams({ section, operation });
    if (search) p.append('search', search);
    p.append('site_id', localStorage.getItem('active_site_id') || 'salem');
    const tok = localStorage.getItem('session_token');
    if (tok) p.append('token', tok);
    const apiBase = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
    const a = document.createElement('a');
    a.href = `${apiBase}/reports/operation-wise-stock/export?${p.toString()}`;
    a.download = `operation_wise_stock_${section}_${operation}.csv`.replace(/ /g, '_').toLowerCase();
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('CSV download started');
  };

  const summaryCards = useMemo(() => {
    const cards = [
      { label: 'Total Bundles', value: summary.total_bundles, color: 'text-indigo-600' },
      { label: 'Total Quantity', value: summary.total_quantity, color: 'text-emerald-600' },
    ];
    Object.entries(summary.by_operation || {}).forEach(([op, count]) => {
      cards.push({ label: op, value: count, color: 'text-stone-700' });
    });
    return cards;
  }, [summary]);

  // Show the Location column only when some row carries a bin footprint
  const hasLocation = useMemo(() => rows.some((r) => r.location), [rows]);

  return (
    <div className="min-h-screen bg-stone-50 p-6" data-testid="ows-report-root">
      <div className="max-w-[1300px] mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="ows-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <Layers className="h-6 w-6 text-indigo-600" />
                Operation Wise Stock Report
              </h1>
              <p className="text-sm text-stone-500">
                WIP stock at each operation — bundle appears under its last completed operation
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => fetchData()} data-testid="ows-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleExport} data-testid="ows-export-btn">
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-stone-600">Section</label>
              <Select value={section} onValueChange={setSection}>
                <SelectTrigger data-testid="ows-section-select"><SelectValue placeholder="Select section" /></SelectTrigger>
                <SelectContent>
                  {sections.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-stone-600">Operation</label>
              <Select value={operation} onValueChange={setOperation}>
                <SelectTrigger data-testid="ows-operation-select"><SelectValue placeholder="Select operation" /></SelectTrigger>
                <SelectContent>
                  {operations.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-stone-600">Search (bundle / sort no / design / color / vendor)</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="h-4 w-4 absolute left-2 top-2.5 text-stone-400" />
                  <Input
                    className="pl-7"
                    placeholder="Type to filter…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchData()}
                    data-testid="ows-search-input"
                  />
                </div>
                <Button onClick={() => fetchData()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ows-apply-btn">
                  Apply
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {summaryCards.map((c) => (
            <Card key={c.label} className="border-stone-200">
              <CardContent className="p-3">
                <div className="text-[11px] uppercase tracking-wider text-stone-500 truncate">{c.label}</div>
                <div className={`text-2xl font-semibold ${c.color}`} data-testid={`ows-summary-${c.label.toLowerCase().replace(/ /g, '-')}`}>
                  {c.value}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Results */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-12 text-center text-stone-500">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-12 text-center text-stone-500" data-testid="ows-empty-state">
                <Boxes className="h-10 w-10 mx-auto mb-2 text-stone-300" />
                No stock found for this selection.
              </div>
            ) : (
              <Table data-testid="ows-results-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Section</TableHead>
                    <TableHead>Operation</TableHead>
                    <TableHead>Bundle Number</TableHead>
                    <TableHead>Sort No</TableHead>
                    <TableHead>FG Name</TableHead>
                    <TableHead>Design Name</TableHead>
                    <TableHead>Color</TableHead>
                    <TableHead>{section === 'Processing' ? 'Processing Unit' : 'Vendor'}</TableHead>
                    {hasLocation && <TableHead>Location</TableHead>}
                    <TableHead className="text-right">Quantity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={`${r.bundle_number}-${i}`} data-testid={`ows-row-${r.bundle_number}`}>
                      <TableCell className="text-xs text-stone-400">{i + 1}</TableCell>
                      <TableCell className="text-xs">{r.section}</TableCell>
                      <TableCell>
                        <Badge className={`${OP_BADGE[r.operation] || 'bg-stone-100 text-stone-700'} border-0 text-[10px]`}>
                          {r.operation || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.bundle_number}</TableCell>
                      <TableCell className="text-xs">{r.sort_no || '—'}</TableCell>
                      <TableCell className="text-xs">{r.fg_name || '—'}</TableCell>
                      <TableCell className="text-xs font-medium">{r.design_name || '—'}</TableCell>
                      <TableCell className="text-xs">{r.color || '—'}</TableCell>
                      <TableCell className="text-xs">{r.vendor_name || '—'}</TableCell>
                      {hasLocation && <TableCell className="text-xs">{r.location || '—'}</TableCell>}
                      <TableCell className="text-right font-medium" data-testid={`ows-qty-${r.bundle_number}`}>
                        {r.quantity ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
